import { useTranslation } from 'react-i18next';
import { ArrowGlyph, type ArrowGlyphVariant } from '../../components/AnnotationGlyph';
import { ReactionArrowGlyph } from '../../components/ReactionArrowGlyph';
import {
  REACTION_ARROW_ANNOTATION_KINDS,
  type ReactionArrowAnnotationKind,
  type ReactionArrowOverlay
} from '@peruselab/structure-editor/chemistry/reactionParticipant';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

interface ReactionArrowsGroupProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  // #413 -- the rail disclosures are a single-open accordion owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
  // ADR 0014 -- one reaction arrow per drawing: the scheme arrows are disabled once it has one.
  readonly schemeArrowPlaced?: boolean;
}

// The left-rail "Reaction arrows" palette (#395). A scheme arrow arms `reaction-arrow-<kind>`
// (or `reaction-arrow-forward.<overlay>` for a "cannot happen" shaft mark); the user then drags
// from a start anchor to an end anchor on the canvas (atom / bond / empty point) to draw a
// decorative annotation -- no reaction-graph meaning (WorkspaceShell -> onReactionArrowPlace).
// The nested "Mechanism arrows" sub-group keeps the electron-pushing curved arrows (ADR 0008
// R5.3): each arms `curved-arrow-<head>`, same drag placement and persistent anchors (#434).

export interface SchemeEntry {
  readonly toolId: string; // the `reaction-arrow-` suffix
  readonly kind: ReactionArrowAnnotationKind;
  readonly overlay?: ReactionArrowOverlay;
  readonly labelKey: string;
}

// Order roughly follows the reference palette in #395: the everyday arrows first, then the
// "blocked" overlays, then rearrangement / dipole, then the vertical forms.
const ORDER: readonly ReactionArrowAnnotationKind[] = [
  'forward',
  'resonance',
  'equilibrium',
  'equilibrium-unequal',
  'retrosynthetic',
  'dashed',
  'wavy',
  'rearrangement',
  'dipole',
  'vertical-up',
  'vertical-equilibrium'
];

const OVERLAYS: readonly ReactionArrowOverlay[] = ['x', 'slash', 'single-bar'];

/** Every scheme arrow the rail offers -- also the Edit tab's arrow-kind picker (#698). */
export const SCHEME_ENTRIES: readonly SchemeEntry[] = [
  ...ORDER.filter((kind) => REACTION_ARROW_ANNOTATION_KINDS.includes(kind)).map((kind) => ({
    toolId: kind,
    kind,
    labelKey: `workspace.rail.reactionArrows.kinds.${kind}`
  })),
  ...OVERLAYS.map((overlay) => ({
    toolId: `forward.${overlay}`,
    kind: 'forward' as const,
    overlay,
    labelKey: `workspace.rail.reactionArrows.overlays.${overlay}`
  }))
];

const MECHANISM: readonly { id: ArrowGlyphVariant; labelKey: string }[] = [
  { id: 'full', labelKey: 'workspace.rail.reactionArrows.mechanism.full' },
  { id: 'half', labelKey: 'workspace.rail.reactionArrows.mechanism.half' },
  { id: 'multi', labelKey: 'workspace.rail.reactionArrows.mechanism.multi' }
];

export function ReactionArrowsGroup({
  tool,
  setTool,
  open,
  onToggle,
  schemeArrowPlaced = false
}: ReactionArrowsGroupProps) {
  const { t } = useTranslation();

  return (
    <details id="rail-group-reactionArrows" className="workspace-rail-annotations" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.reactionArrows.groupLabel')}
      </summary>
      <div
        className="workspace-rail-annotations-body"
        role="group"
        aria-label={t('workspace.rail.reactionArrows.controlsLabel')}
      >
        <div className="workspace-rail-annotations-grid workspace-rail-annotations-grid--triple">
          {SCHEME_ENTRIES.map(({ toolId, kind, overlay, labelKey }) => {
            const armed: EditorTool = `reaction-arrow-${toolId}`;
            const active = tool === armed;
            const name = schemeArrowPlaced
              ? `${t(labelKey)} — ${t('workspace.rail.reactionArrows.oneArrow')}`
              : t(labelKey);
            return (
              <button
                key={toolId}
                type="button"
                className={`icon-button ${active ? 'active' : ''}`}
                aria-label={name}
                title={name}
                aria-pressed={active}
                disabled={schemeArrowPlaced}
                onClick={() => setTool(armed)}
              >
                <ReactionArrowGlyph kind={kind} {...(overlay ? { overlay } : {})} />
              </button>
            );
          })}
        </div>

        <p className="workspace-rail-annotations-subhead">{t('workspace.rail.reactionArrows.mechanism.label')}</p>
        <div className="workspace-rail-annotations-grid workspace-rail-annotations-grid--triple">
          {MECHANISM.map(({ id, labelKey }) => {
            const active = tool === `curved-arrow-${id}`;
            return (
              <button
                key={id}
                type="button"
                className={`icon-button ${active ? 'active' : ''}`}
                aria-label={t(labelKey)}
                title={t(labelKey)}
                aria-pressed={active}
                onClick={() => setTool(`curved-arrow-${id}`)}
              >
                <ArrowGlyph variant={id} />
              </button>
            );
          })}
        </div>

        <p className="workspace-rail-annotations-hint">{t('workspace.rail.reactionArrows.hint')}</p>
      </div>
    </details>
  );
}
